/**
 * Permission Directive
 *
 * Usage:
 * v-permission="'STOCKS'"                          // Check module view permission
 * v-permission="{ module: 'STOCKS' }"              // Check module view permission
 * v-permission="{ module: 'STOCKS', action: 'CanAdd' }" // Check specific action
 * v-permission="{ subModule: 'STOCK_INFO' }"       // Check submodule view permission
 * v-permission="{ subModule: 'STOCK_INFO', action: 'CanUpdate' }" // Check submodule action
 * v-permission="{ module: 'STOCKS', subModule: 'STOCK_INFO' }" // Check both
 *
 * Examples:
 * <button v-permission="'STOCKS'">View Stocks</button>
 * <button v-permission="{ module: 'STOCKS', action: 'CanAdd' }">Add Stock</button>
 * <button v-permission="{ subModule: 'STOCK_INFO', action: 'CanDelete' }">Delete</button>
 */

import store from '../store'

function checkPermission(binding) {
  const value = binding.value

  // SuperAdmin has all permissions
  if (store.state.isSuperAdmin) {
    return true
  }

  // If value is a string, treat it as module code with CanView permission
  if (typeof value === 'string') {
    return store.getters.hasModulePermission(value, 'CanView')
  }

  // If value is an object
  if (typeof value === 'object' && value !== null) {
    const { module, subModule, action = 'CanView' } = value

    // Check both module and submodule if both are provided
    if (module && subModule) {
      const hasModulePerm = store.getters.hasModulePermission(module, action)
      const hasSubModulePerm = store.getters.hasSubModulePermission(subModule, action)
      return hasModulePerm && hasSubModulePerm
    }

    // Check only module permission
    if (module) {
      return store.getters.hasModulePermission(module, action)
    }

    // Check only submodule permission
    if (subModule) {
      return store.getters.hasSubModulePermission(subModule, action)
    }
  }

  // Default to no permission if format is invalid
  console.warn('v-permission: Invalid permission value', binding.value)
  return false
}

export default {
  // Vue 2 directive hooks
  bind(el, binding, vnode) {
    const hasPermission = checkPermission(binding)

    if (!hasPermission) {
      // Store the original display style
      el.style.display = 'none'
      el.setAttribute('data-permission-hidden', 'true')
    }
  },

  update(el, binding, vnode) {
    const hasPermission = checkPermission(binding)

    if (!hasPermission) {
      el.style.display = 'none'
      el.setAttribute('data-permission-hidden', 'true')
    } else {
      // Only restore display if it was hidden by permission
      if (el.getAttribute('data-permission-hidden') === 'true') {
        el.style.display = ''
        el.removeAttribute('data-permission-hidden')
      }
    }
  },

  // Cleanup
  unbind(el) {
    el.removeAttribute('data-permission-hidden')
  }
}
